import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Header } from "./components";
import { AppSidebar } from "./components/app-sidebar";
import { Button } from "./components/ui/button";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { MicrosoftLogo } from "./icons";
import { RequireAllocationsAccess } from "./components/RequireAllocationsAccess";
import { RequireDealerDirectQueueAccess, RequireUnderwritingQueueAccess } from "./components/RequireUnderwritingAccess";
import { Allocations } from "./pages/Allocations";
import { AllocationReportPage } from "./pages/AllocationReport";
import { DealerDetail } from "./pages/DealerDetail";
import { DealerSearch } from "./pages/DealerSearch";
import { InsightsReportsPage } from "./pages/InsightsReports";
import { Pipeline } from "./pages/Pipeline";
import { UnderwritingDashboard } from "./pages/UnderwritingDashboard";
import { UnderwritingQueue } from "./pages/UnderwritingQueue";
import { UnderwritingQueueAuditLog } from "./pages/UnderwritingQueueAuditLog";
import { AllRequests } from "./pages/AllRequests";
import { UnderwritingApplicationDetailPage } from "./pages/UnderwritingApplicationDetail";
import { UnderwritingQueueApplicationDetailPage } from "./pages/UnderwritingQueueApplicationDetail";
import { setPhoenixToken, setSsoToken } from "./services/apiClient";
import { AuthService } from "./services/AuthService";
import { isOfflineMode } from "./helpers/offlineMode";

function AuthenticatedContent() {
  const isProduction = import.meta.env.MODE === "production";
  const headerHeightClass = isProduction
    ? "[--header-height:calc(var(--spacing)*14)]"
    : "[--header-height:calc(var(--spacing)*22)] 2xl:[--header-height:calc(var(--spacing)*28)]";

  return (
    <div className={headerHeightClass}>
      <SidebarProvider open={false} onOpenChange={() => {}} className="flex flex-col">
        <Header />
        <div className="flex flex-1 pt-(--header-height)">
          <AppSidebar />
          <SidebarInset>
            <Routes>
              <Route path="/" element={<Navigate to="/pipeline" replace />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/insights-reports" element={<InsightsReportsPage />} />
              <Route path="/underwriting-queue" element={<RequireUnderwritingQueueAccess><UnderwritingDashboard /></RequireUnderwritingQueueAccess>} />
              <Route path="/underwriting-queue/all-requests" element={<RequireUnderwritingQueueAccess><AllRequests /></RequireUnderwritingQueueAccess>} />
              <Route path="/dealer-direct-prop-queue" element={<RequireDealerDirectQueueAccess><UnderwritingQueue /></RequireDealerDirectQueueAccess>} />
              <Route path="/dealer-direct-prop-queue/audit-log" element={<RequireDealerDirectQueueAccess><UnderwritingQueueAuditLog /></RequireDealerDirectQueueAccess>} />
              <Route path="/dealer-direct-prop-queue/:applicationId" element={<RequireDealerDirectQueueAccess><UnderwritingQueueApplicationDetailPage /></RequireDealerDirectQueueAccess>} />
              <Route path="/underwriting-queue/:applicationId" element={<RequireUnderwritingQueueAccess><UnderwritingApplicationDetailPage /></RequireUnderwritingQueueAccess>} />
              <Route path="/allocation-report" element={<AllocationReportPage />} />
              <Route path="/dealer-search" element={<DealerSearch />} />
              <Route path="/dealer-search/:dealerId" element={<DealerDetail />} />
              <Route
                path="/allocations"
                element={
                  <RequireAllocationsAccess>
                    <Allocations />
                  </RequireAllocationsAccess>
                }
              />
            </Routes>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
}

function OnlineApp() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const authService = useMemo(() => new AuthService(), []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const exchangeToken = async () => {
      try {
        const { phoenixToken, ssoToken } = await authService.authenticate(instance);
        setPhoenixToken(phoenixToken.authenticationToken);
        setSsoToken(ssoToken);
      } catch (error) {
        console.error("Failed to exchange token:", error);
      }
    };

    exchangeToken();
  }, [isAuthenticated, instance, authService]);

  const handleLogin = () => {
    instance.loginRedirect({
      scopes: ["User.Read"],
    });
  };

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      handleLogin();
    }
  }, [isAuthenticated, inProgress, instance]);

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedContent />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="bg-primary-200 flex h-screen w-full items-center justify-center">
          <Button onClick={handleLogin}>
            <MicrosoftLogo />
            Sign in with Microsoft.
          </Button>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

function OfflineApp() {
  return <AuthenticatedContent />;
}

function App() {
  return (
    <>
      <Toaster position="bottom-right" richColors />
      {isOfflineMode() ? <OfflineApp /> : <OnlineApp />}
    </>
  );
}

export default App;
